import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../services/http.js';
import { useToast } from '../hooks/useToast.jsx';
import { ErrorRetryCard } from '../components/common/ErrorRetryCard.jsx';

function ProgressBar({ current, total }) {
  const pct = total > 0 ? Math.min(100, Math.round(((current - 1) / total) * 100)) : 0;
  return (
    <div>
      <div className="flex justify-between text-xs text-on-surface-variant mb-1">
        <span>Dia {current} de {total}</span>
        <span>{pct}%</span>
      </div>
      <div className="bg-surface-container-highest rounded-full h-2 overflow-hidden">
        <div className="bg-primary h-full" style={{ width: `${pct}%` }}></div>
      </div>
    </div>
  );
}

const FREQUENCY_LABELS = {
  diaria: 'Todos os dias',
  dias_especificos: 'Dias específicos da semana',
  semanal: 'Uma vez por semana',
};

const WEEKDAY_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

function ScheduleForm({ enrollment, onSaved, onCancel }) {
  const [frequency, setFrequency] = useState(enrollment.schedule?.frequency || 'diaria');
  const [daysOfWeek, setDaysOfWeek] = useState(enrollment.schedule?.days_of_week || []);
  const [preferredTime, setPreferredTime] = useState(enrollment.schedule?.preferred_time || '');
  const [timezone, setTimezone] = useState(enrollment.timezone || 'America/Sao_Paulo');
  const [reminderEnabled, setReminderEnabled] = useState(!!enrollment.reminder_enabled);
  const [saving, setSaving] = useState(false);
  const { showApiErrorToast, showToast } = useToast();

  const toggleDay = (d) => {
    setDaysOfWeek((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort()));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { frequency, preferred_time: preferredTime || null, timezone, reminder_enabled: reminderEnabled };
      if (frequency === 'dias_especificos') payload.days_of_week = daysOfWeek;
      const { res, data } = await apiClient.request(`/reading/enrollments/${enrollment.enrollment_id}/schedule`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        showToast({ type: 'success', message: 'Agenda atualizada. Seu progresso foi preservado.' });
        onSaved(data);
      } else {
        showApiErrorToast(res, data, 'Não foi possível atualizar a agenda.');
      }
    } catch {
      showApiErrorToast(null, null, 'Não foi possível atualizar a agenda.');
    }
    setSaving(false);
  };

  return (
    <div className="bg-surface-container-highest rounded-lg p-4 space-y-3 mt-2">
      <div>
        <label htmlFor={`freq-${enrollment.enrollment_id}`} className="text-xs font-medium text-on-surface-variant">
          Frequência
        </label>
        <select
          id={`freq-${enrollment.enrollment_id}`}
          value={frequency}
          onChange={(e) => setFrequency(e.target.value)}
          className="w-full mt-1 rounded border border-outline-variant bg-surface p-2 text-sm"
        >
          {Object.entries(FREQUENCY_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </div>

      {frequency === 'dias_especificos' && (
        <div>
          <span className="text-xs font-medium text-on-surface-variant block mb-1">Dias da semana</span>
          <div className="flex gap-1 flex-wrap" role="group" aria-label="Dias da semana">
            {WEEKDAY_LABELS.map((label, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => toggleDay(idx)}
                aria-pressed={daysOfWeek.includes(idx)}
                className={`px-2 py-1 rounded text-xs border ${
                  daysOfWeek.includes(idx)
                    ? 'bg-primary text-on-primary border-primary'
                    : 'border-outline-variant text-on-surface-variant'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div>
        <label htmlFor={`time-${enrollment.enrollment_id}`} className="text-xs font-medium text-on-surface-variant">
          Horário preferido (opcional)
        </label>
        <input
          id={`time-${enrollment.enrollment_id}`}
          type="time"
          value={preferredTime}
          onChange={(e) => setPreferredTime(e.target.value)}
          className="w-full mt-1 rounded border border-outline-variant bg-surface p-2 text-sm"
        />
      </div>

      <div>
        <label htmlFor={`tz-${enrollment.enrollment_id}`} className="text-xs font-medium text-on-surface-variant">
          Fuso horário
        </label>
        <input
          id={`tz-${enrollment.enrollment_id}`}
          type="text"
          value={timezone}
          onChange={(e) => setTimezone(e.target.value)}
          placeholder="America/Sao_Paulo"
          className="w-full mt-1 rounded border border-outline-variant bg-surface p-2 text-sm"
        />
      </div>

      <label className="flex items-center gap-2 text-xs font-medium text-on-surface-variant">
        <input
          type="checkbox"
          checked={reminderEnabled}
          onChange={(e) => setReminderEnabled(e.target.checked)}
          className="rounded border-outline-variant"
        />
        Ativar lembretes por e-mail para esta leitura
      </label>

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 text-sm rounded border border-outline-variant text-on-surface-variant hover:bg-surface-container-high"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="px-3 py-1.5 text-sm bg-primary text-on-primary font-bold rounded hover:opacity-90 disabled:opacity-50"
        >
          Salvar agenda
        </button>
      </div>
    </div>
  );
}

function ActiveEnrollmentCard({ enrollment, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const { showApiErrorToast, showToast } = useToast();

  const handlePause = async () => {
    setBusy(true);
    try {
      const { res, data } = await apiClient.request(`/reading/enrollments/${enrollment.enrollment_id}/pause`, { method: 'POST' });
      if (res.ok) {
        showToast({ type: 'success', message: 'Leitura pausada. Seu progresso continua salvo.' });
        onChanged();
      } else {
        showApiErrorToast(res, data, 'Não foi possível pausar esta leitura.', { onRetry: handlePause });
      }
    } catch {
      showApiErrorToast(null, null, 'Não foi possível pausar esta leitura.', { onRetry: handlePause });
    }
    setBusy(false);
  };

  return (
    <div className="bg-surface-container-low border border-outline-variant rounded-lg p-5 space-y-3">
      <div className="flex justify-between items-start gap-3">
        <div>
          <p className="font-bold text-lg">{enrollment.plan_title}</p>
          <p className="text-xs text-on-surface-variant/70 mt-1">
            {enrollment.progress_count} {enrollment.progress_count === 1 ? 'dia concluído' : 'dias concluídos'}
          </p>
        </div>
        <span className="bg-surface-container-high text-on-surface px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap">
          Em andamento
        </span>
      </div>

      <ProgressBar current={enrollment.current_day} total={enrollment.duration_days} />

      {enrollment.today && (
        <div className="bg-surface-container-highest rounded-lg p-3">
          <p className="text-xs text-on-surface-variant uppercase tracking-wide font-bold mb-1">Leitura de hoje</p>
          <p className="font-semibold">{enrollment.today.title}</p>
        </div>
      )}

      <div className="flex flex-wrap justify-end gap-2">
        <button
          type="button"
          onClick={() => setShowSchedule((v) => !v)}
          className="px-4 py-2 bg-surface-container-high text-on-surface font-bold rounded hover:opacity-90 text-sm"
        >
          Reagendar
        </button>
        <button
          type="button"
          onClick={handlePause}
          disabled={busy}
          className="px-4 py-2 bg-surface-container-high text-on-surface font-bold rounded hover:opacity-90 disabled:opacity-50 text-sm"
        >
          Pausar
        </button>
        <Link
          to={`/leitura/${enrollment.enrollment_id}/dia/${enrollment.current_day}`}
          className="px-4 py-2 bg-primary text-on-primary font-bold rounded hover:opacity-90 text-sm"
        >
          Continuar leitura de hoje
        </Link>
      </div>

      {showSchedule && (
        <ScheduleForm
          enrollment={enrollment}
          onCancel={() => setShowSchedule(false)}
          onSaved={() => { setShowSchedule(false); onChanged(); }}
        />
      )}
    </div>
  );
}

function HistoryEnrollmentCard({ enrollment, onChanged }) {
  const [busy, setBusy] = useState(false);
  const { showApiErrorToast, showToast } = useToast();

  const statusLabel = {
    paused: 'Pausada',
    completed: 'Concluída',
    abandoned: 'Abandonada',
  }[enrollment.status] || enrollment.status;

  const handleResume = async () => {
    setBusy(true);
    try {
      const { res, data } = await apiClient.request(`/reading/enrollments/${enrollment.enrollment_id}/resume`, { method: 'POST' });
      if (res.ok) {
        showToast({ type: 'success', message: 'Leitura retomada de onde você parou.' });
        onChanged();
      } else {
        showApiErrorToast(res, data, 'Não foi possível retomar esta leitura.', { onRetry: handleResume });
      }
    } catch {
      showApiErrorToast(null, null, 'Não foi possível retomar esta leitura.', { onRetry: handleResume });
    }
    setBusy(false);
  };

  return (
    <div className="bg-surface-container-low border border-outline-variant rounded-lg p-4 flex items-center justify-between gap-3">
      <div>
        <p className="font-semibold">{enrollment.plan_title}</p>
        <p className="text-xs text-on-surface-variant">
          {statusLabel} · Dia {enrollment.current_day} de {enrollment.duration_days} · {enrollment.progress_count} concluídos
        </p>
      </div>
      {enrollment.status === 'paused' && (
        <button
          type="button"
          onClick={handleResume}
          disabled={busy}
          className="px-3 py-1.5 text-sm bg-primary text-on-primary font-bold rounded hover:opacity-90 disabled:opacity-50 whitespace-nowrap"
        >
          Retomar
        </button>
      )}
    </div>
  );
}

function RecommendationCard({ recommendation, onEnrolled }) {
  const [busy, setBusy] = useState(false);
  const { showApiErrorToast, showToast } = useToast();

  if (!recommendation?.plan) return null;

  const handleEnroll = async () => {
    setBusy(true);
    try {
      const { res, data } = await apiClient.request('/reading/enrollments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan_id: recommendation.plan.id }),
      });
      if (res.ok) {
        showToast({ type: 'success', message: `Inscrição em "${recommendation.plan.title}" iniciada.` });
        onEnrolled();
      } else {
        showApiErrorToast(res, data, 'Não foi possível iniciar este plano.', { onRetry: handleEnroll });
      }
    } catch {
      showApiErrorToast(null, null, 'Não foi possível iniciar este plano.', { onRetry: handleEnroll });
    }
    setBusy(false);
  };

  return (
    <div className="bg-primary-container/40 border border-primary/30 rounded-lg p-5 space-y-3">
      <p className="text-xs font-bold uppercase tracking-wide text-primary">Recomendado para você</p>
      <p className="font-bold text-lg">{recommendation.plan.title}</p>
      <p className="text-sm text-on-surface-variant">{recommendation.plan.description}</p>
      <ul className="text-sm text-on-surface-variant list-disc list-inside space-y-1">
        {recommendation.reasons.map((reason, idx) => (
          <li key={idx}>{reason}</li>
        ))}
      </ul>
      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleEnroll}
          disabled={busy}
          className="px-4 py-2 bg-primary text-on-primary font-bold rounded hover:opacity-90 disabled:opacity-50 text-sm"
        >
          Começar este plano
        </button>
      </div>
    </div>
  );
}

function CatalogSection({ plans, onEnrolled }) {
  const [busyId, setBusyId] = useState(null);
  const { showApiErrorToast, showToast } = useToast();

  const handleEnroll = async (plan) => {
    setBusyId(plan.id);
    try {
      const { res, data } = await apiClient.request('/reading/enrollments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan_id: plan.id }),
      });
      if (res.ok) {
        showToast({ type: 'success', message: `Inscrição em "${plan.title}" iniciada.` });
        onEnrolled();
      } else {
        showApiErrorToast(res, data, 'Não foi possível iniciar este plano.', { onRetry: () => handleEnroll(plan) });
      }
    } catch {
      showApiErrorToast(null, null, 'Não foi possível iniciar este plano.', { onRetry: () => handleEnroll(plan) });
    }
    setBusyId(null);
  };

  return (
    <div className="space-y-3">
      <h2 className="font-bold text-sm text-on-surface-variant uppercase tracking-wide">Todos os planos</h2>
      {plans.map((plan) => (
        <div key={plan.id} className="bg-surface-container-low border border-outline-variant rounded-lg p-4 flex items-center justify-between gap-3">
          <div>
            <p className="font-semibold">{plan.title}</p>
            <p className="text-sm text-on-surface-variant">{plan.description}</p>
            <p className="text-xs text-on-surface-variant/70 mt-1">{plan.duration_days} dias</p>
          </div>
          <button
            type="button"
            onClick={() => handleEnroll(plan)}
            disabled={busyId === plan.id}
            className="px-4 py-2 bg-primary text-on-primary font-bold rounded hover:opacity-90 disabled:opacity-50 text-sm whitespace-nowrap"
          >
            Inscrever-se
          </button>
        </div>
      ))}
    </div>
  );
}

export default function LeituraPage() {
  const [dashboard, setDashboard] = useState(null);
  const [dashboardError, setDashboardError] = useState(null);
  const [recommendation, setRecommendation] = useState(null);
  const [recommendationError, setRecommendationError] = useState(null);
  const [plans, setPlans] = useState(null);
  const [plansError, setPlansError] = useState(null);

  // Cada seção busca e falha de forma independente: uma falha parcial (ex.:
  // recomendação indisponível) não impede o resto da página de renderizar.
  const fetchDashboard = async () => {
    setDashboardError(null);
    try {
      const { res, data } = await apiClient.request('/reading/me/dashboard');
      if (res.ok) {
        setDashboard(data);
        return;
      }
      setDashboardError(apiClient.parseApiError(data, 'Não foi possível carregar seu progresso de leitura.'));
    } catch {
      setDashboardError('Falha de conexão ao carregar seu progresso de leitura.');
    }
  };

  const fetchRecommendation = async () => {
    setRecommendationError(null);
    try {
      const { res, data } = await apiClient.request('/reading/recommendation');
      if (res.ok) {
        setRecommendation(data);
        return;
      }
      setRecommendationError(apiClient.parseApiError(data, 'Não foi possível carregar a recomendação.'));
    } catch {
      setRecommendationError('Falha de conexão ao carregar a recomendação.');
    }
  };

  const fetchPlans = async () => {
    setPlansError(null);
    try {
      const { res, data } = await apiClient.request('/reading/plans');
      if (res.ok) {
        setPlans(data);
        return;
      }
      setPlansError(apiClient.parseApiError(data, 'Não foi possível carregar o catálogo de planos.'));
    } catch {
      setPlansError('Falha de conexão ao carregar o catálogo de planos.');
    }
  };

  const refetchAll = () => {
    fetchDashboard();
    fetchRecommendation();
    fetchPlans();
  };

  useEffect(() => { setTimeout(refetchAll, 0); }, []);

  const hasActive = dashboard?.active_enrollments?.length > 0;
  const isNewUser = dashboard && !hasActive && (!dashboard.history || dashboard.history.length === 0);

  return (
    <div className="min-h-screen bg-surface text-on-surface p-6">
      <div className="max-w-3xl mx-auto space-y-8">
        <div className="flex items-center justify-between">
          <Link to="/chat" className="hidden md:inline-flex items-center gap-2 px-4 py-2 bg-surface-container-low hover:bg-surface-container-high rounded-lg font-medium transition-colors border border-outline-variant">
            <span>←</span> Voltar para o Chat
          </Link>
          <h1 className="text-2xl font-bold">Leitura Assistida</h1>
        </div>

        <p className="text-on-surface-variant text-sm">
          Leia a Bíblia com uma sequência guiada, no seu ritmo. Concluir um dia não consome créditos.
        </p>

        {dashboard === null ? (
          dashboardError ? (
            <ErrorRetryCard message={dashboardError} onRetry={fetchDashboard} />
          ) : (
            <p className="text-sm text-on-surface-variant">Carregando seu progresso...</p>
          )
        ) : (
          <>
            {isNewUser && (
              <div className="bg-surface-container-low border border-outline-variant rounded-lg p-4 text-sm text-on-surface-variant">
                Bem-vindo(a)! Escolha um plano abaixo (ou aceite nossa recomendação) para começar sua leitura guiada hoje mesmo.
              </div>
            )}

            {hasActive && (
              <div className="space-y-4">
                <h2 className="font-bold text-sm text-on-surface-variant uppercase tracking-wide">Sua leitura ativa</h2>
                {dashboard.active_enrollments.map((enrollment) => (
                  <ActiveEnrollmentCard key={enrollment.enrollment_id} enrollment={enrollment} onChanged={refetchAll} />
                ))}
              </div>
            )}

            {!hasActive && (
              recommendationError ? (
                <ErrorRetryCard message={recommendationError} onRetry={fetchRecommendation} />
              ) : recommendation === null ? (
                <p className="text-sm text-on-surface-variant">Carregando recomendação...</p>
              ) : (
                <RecommendationCard recommendation={recommendation} onEnrolled={refetchAll} />
              )
            )}

            {dashboard.history && dashboard.history.length > 0 && (
              <div className="space-y-3">
                <h2 className="font-bold text-sm text-on-surface-variant uppercase tracking-wide">Histórico</h2>
                {dashboard.history.map((enrollment) => (
                  <HistoryEnrollmentCard key={enrollment.enrollment_id} enrollment={enrollment} onChanged={refetchAll} />
                ))}
              </div>
            )}
          </>
        )}

        {plansError ? (
          <ErrorRetryCard message={plansError} onRetry={fetchPlans} />
        ) : plans === null ? (
          <p className="text-sm text-on-surface-variant">Carregando planos...</p>
        ) : (
          <CatalogSection
            plans={plans.filter((p) => !dashboard?.active_enrollments?.some((e) => e.plan_id === p.id))}
            onEnrolled={refetchAll}
          />
        )}
      </div>
    </div>
  );
}
